import { useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { t } from '../i18n.ts'
import { useAuth } from '../store/AuthProvider.tsx'
import './AuthButton.css'

/** Firebase error codes arrive as `code` on the error object, e.g. `auth/popup-blocked`. */
function describeSignInError(error: unknown): string {
  const code = typeof error === 'object' && error !== null ? String(Reflect.get(error, 'code') ?? '') : ''
  if (code === 'auth/unauthorized-domain') return t.auth.signInUnauthorizedDomain
  return t.auth.signInFailed
}

/**
 * The offer to sign in, from the top bar.
 *
 * Where it sits is the whole of it. This used to be a button at the foot of the day
 * view, under twenty-two concert rows — so a signed-out user met every local thing the
 * app does before learning there was anything to sign in to, and on the friends page,
 * the one screen that cannot work without an account, the offer was not on the screen
 * at all. The header is rendered on every route and sticks to the top of it, which is
 * the only place in this app that is true of.
 *
 * Only the offer moved. `AuthButton` keeps signing out and deleting your data where
 * they were: those are things you go looking for once, not things a bar should hold in
 * front of you for the length of a festival.
 *
 * Renders nothing when no cloud project is configured — there is nothing to sign in to,
 * and an inert button in the header would raise a question the app cannot answer.
 */
export function SignInAction() {
  const { ready, cloudEnabled, user, signIn } = useAuth()
  const [busy, setBusy] = useState(false)
  const [signInError, setSignInError] = useState<string | null>(null)
  const { pathname } = useLocation()

  /*
   * The message goes when the moment it belongs to does.
   *
   * The bar is rendered on every route, so without this a failure on the friends page
   * followed the reader around the app — still hanging over the map two taps later,
   * describing something they had stopped trying to do. It reports one attempt, not a
   * state of the world, and leaving the page is as good as saying «yes, I saw it».
   */
  useEffect(() => {
    setSignInError(null)
  }, [pathname])

  /*
   * Escape, because the panel covers the top of whatever page is underneath it and a
   * keyboard user should not have to go looking for the one control that moves it.
   * Bound to the document rather than the panel: nothing here takes focus when the
   * message appears, so there is no element for a key to arrive at.
   */
  useEffect(() => {
    if (!signInError) return
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') setSignInError(null)
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [signInError])

  if (!ready || !cloudEnabled || user) return null

  return (
    <>
      <button
        type="button"
        className="app-header__signin"
        disabled={busy}
        // Short on the bar, whole to a screen reader — see `signInShort`.
        aria-label={t.auth.signIn}
        onClick={() => {
          setBusy(true)
          setSignInError(null)
          /*
           * `.catch` matters more than it looks. signInWithRedirect rejects before
           * navigating whenever the origin is missing from Firebase's authorized
           * domains, and this was previously `void signIn().finally(...)` — which
           * does not catch. The rejection went nowhere, the button re-enabled
           * itself, and the whole failure read as an unresponsive button.
           */
          signIn()
            .catch((error: unknown) => setSignInError(describeSignInError(error)))
            .finally(() => setBusy(false))
        }}
      >
        {t.auth.signInShort}
      </button>
      {/*
        Hung under the bar rather than squeezed into it, the way the import panel is:
        `auth/unauthorized-domain` is two lines of instruction naming a setup script,
        and the message explaining why nobody can log in is the last one to truncate.

        Hanging over the page is the price of not resizing the header — `--header-height`
        is arithmetic the map page does to fill the screen, and a bar that grew by two
        lines would take the map with it. So it overlaps, and earns that by being
        dismissible: a close control, Escape, the next attempt, or leaving the page.
        What it must not do is sit on top of a heading until somebody tries to sign in
        again, which is what it did.

        `role="alert"` is on the message alone. On the wrapper it would take the close
        button's label into the announcement, so the failure would read out with a
        «Lukk feilmeldingen» stuck on the end of it.
      */}
      {signInError && (
        <div className="app-header__auth-error">
          <p className="app-header__auth-error-text" role="alert">
            {signInError}
          </p>
          <button
            type="button"
            className="app-header__auth-error-close"
            aria-label={t.auth.dismissError}
            onClick={() => setSignInError(null)}
          >
            <CloseIcon />
          </button>
        </div>
      )}
    </>
  )
}

function CloseIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M6 6l12 12M18 6L6 18"
        stroke="currentColor"
        strokeWidth="2.2"
        strokeLinecap="round"
      />
    </svg>
  )
}

/**
 * Signing out, and the data-removal control.
 *
 * Renders nothing at all when no cloud project is configured — there is nothing to
 * sign in to, and an inert button would just raise a question the app cannot answer.
 */
export function AuthButton() {
  const { ready, cloudEnabled, user, signOut, deleteMyData } = useAuth()
  const [confirmingDelete, setConfirmingDelete] = useState(false)
  const [busy, setBusy] = useState(false)

  if (!ready || !cloudEnabled) return null

  if (!user) {
    /*
     * The fact, with the offer gone from underneath it.
     *
     * The button that used to be here is in the top bar now (`SignInAction`). The
     * sentence stays: nothing else in the app tells anyone that their notes live on
     * this device and nowhere else, and that is the entire reason the button up there
     * is worth finding. Reporting the state without repeating the control is also why
     * it is at the foot of the list — it answers a question, it does not ask one.
     */
    return (
      <div className="auth">
        <p className="auth__note">{t.auth.localOnly}</p>
      </div>
    )
  }

  return (
    <div className="auth">
      <p className="auth__note">{t.auth.signedInAs(user.name)}</p>
      <div className="auth__row">
        <button type="button" className="auth__secondary" onClick={() => void signOut()}>
          {t.auth.signOut}
        </button>

        {confirmingDelete ? (
          <>
            <button
              type="button"
              className="auth__danger"
              disabled={busy}
              onClick={() => {
                setBusy(true)
                void deleteMyData().finally(() => setBusy(false))
              }}
            >
              {t.auth.deleteConfirm}
            </button>
            <button
              type="button"
              className="auth__secondary"
              onClick={() => setConfirmingDelete(false)}
            >
              {t.auth.deleteCancel}
            </button>
          </>
        ) : (
          <button
            type="button"
            className="auth__secondary"
            onClick={() => setConfirmingDelete(true)}
          >
            {t.auth.deleteData}
          </button>
        )}
      </div>
      {confirmingDelete && <p className="auth__warning">{t.auth.deleteWarning}</p>}
    </div>
  )
}
